import { useMemo } from 'react';
import { formatBidMoney, type Bid, type BidStatus } from '../bidStore';
import './BidSchedule.css';

interface BidScheduleProps {
  bids: Bid[];
  onBidClick?: (bid: Bid) => void;
}

const STATUS_COLOR: Record<BidStatus, string> = {
  Draft: '#6c757d',
  'In Review': '#17a2b8',
  Submitted: '#ffc107',
  Won: '#28a745',
  Lost: '#dc3545',
};

const DAY_MS = 86_400_000;

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/**
 * A lightweight Gantt-style timeline of bids, each spanning created → due date.
 * Self-contained (no charting lib) so it carries zero backend/runtime risk.
 */
const BidSchedule = ({ bids, onBidClick }: BidScheduleProps): JSX.Element => {
  const { rows, months, rangeStart, totalDays } = useMemo(() => {
    const valid = bids.filter((b) => b.createdDate && b.dueDate);
    if (valid.length === 0) {
      return { rows: [] as Bid[], months: [] as Date[], rangeStart: new Date(), totalDays: 1 };
    }

    let min = Infinity;
    let max = -Infinity;
    valid.forEach((b) => {
      const s = new Date(b.createdDate).getTime();
      const e = new Date(b.dueDate).getTime();
      if (s < min) min = s;
      if (e > max) max = e;
    });

    const start = startOfMonth(new Date(min));
    const endExclusive = addMonths(startOfMonth(new Date(max)), 1);
    const total = Math.max(1, Math.round((endExclusive.getTime() - start.getTime()) / DAY_MS));

    const monthList: Date[] = [];
    let cursor = new Date(start);
    while (cursor < endExclusive) {
      monthList.push(new Date(cursor));
      cursor = addMonths(cursor, 1);
    }

    const sorted = [...valid].sort(
      (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
    );

    return { rows: sorted, months: monthList, rangeStart: start, totalDays: total };
  }, [bids]);

  if (rows.length === 0) {
    return (
      <div className="bid-schedule-empty">
        <div className="bid-schedule-empty-icon">📅</div>
        <h3>No bids to schedule</h3>
        <p>Bids with a created and due date will appear on this timeline.</p>
      </div>
    );
  }

  const pct = (date: string): number => {
    const offsetDays = (new Date(date).getTime() - rangeStart.getTime()) / DAY_MS;
    return (offsetDays / totalDays) * 100;
  };

  const today = new Date();
  const todayPct = pct(today.toISOString());
  const showToday = todayPct >= 0 && todayPct <= 100;

  return (
    <div className="bid-schedule">
      <div className="bid-schedule-grid">
        {/* Month header */}
        <div className="bid-schedule-header">
          <div className="bid-schedule-label-col">Bid</div>
          <div className="bid-schedule-track-col">
            <div className="bid-schedule-months">
              {months.map((m) => {
                const width =
                  (new Date(m.getFullYear(), m.getMonth() + 1, 1).getTime() - m.getTime()) /
                  DAY_MS /
                  totalDays *
                  100;
                return (
                  <div key={m.toISOString()} className="bid-schedule-month" style={{ width: `${width}%` }}>
                    {m.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Rows */}
        <div className="bid-schedule-body">
          {showToday && (
            <div className="bid-schedule-today" style={{ left: `calc(220px + (100% - 220px) * ${todayPct / 100})` }}>
              <span className="bid-schedule-today-label">Today</span>
            </div>
          )}
          {rows.map((bid) => {
            const left = pct(bid.createdDate);
            const right = pct(bid.dueDate);
            const width = Math.max(2, right - left);
            return (
              <div className="bid-schedule-row" key={bid.id}>
                <div className="bid-schedule-label-col" title={bid.name}>
                  <div className="bid-schedule-row-name">{bid.name}</div>
                  <div className="bid-schedule-row-id">{bid.id}</div>
                </div>
                <div className="bid-schedule-track-col">
                  <button
                    type="button"
                    className="bid-schedule-bar"
                    style={{ left: `${left}%`, width: `${width}%`, backgroundColor: STATUS_COLOR[bid.status] }}
                    onClick={() => onBidClick?.(bid)}
                    title={`${bid.name} · ${bid.status} · ${formatBidMoney(bid.bidValue)}`}
                  >
                    <span className="bid-schedule-bar-text">
                      {bid.status} · {formatBidMoney(bid.bidValue)}
                    </span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bid-schedule-legend">
        {(Object.keys(STATUS_COLOR) as BidStatus[]).map((s) => (
          <span key={s} className="bid-schedule-legend-item">
            <span className="bid-schedule-legend-dot" style={{ backgroundColor: STATUS_COLOR[s] }} />
            {s}
          </span>
        ))}
      </div>
    </div>
  );
};

export default BidSchedule;
